import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {
  LINE_ITEM_TYPES,
  PAYMENT_REQUEST_CATEGORIES
} from 'dpl/shared/constants/breeder_app';
import { getDateString } from 'dpl/shared/utils/date';

import { ensureCurrencyIsPresent } from './util';
import StripeChargeModalPaymentSummaryLineItemV2 from './StripeChargeModalPaymentSummaryLineItemV2';
import StripeChargeModalPaymentProcessingLineItem from './StripeChargeModalPaymentProcessingLineItem';
import StripeChargeModalPlatformFeeLineItem from './StripeChargeModalPlatformFeeLineItem';
import StripeChargeModalDepositLineItem from './StripeChargeModalDepositLineItem';
import { PAYMENT_TIER_TYPE } from './constants';

const { PROCESSING_FEE, FEE, CHARGE } = LINE_ITEM_TYPES;

export default function StripeChargeModalPaymentSummaryV3({
  amountTotal,
  amountSubtotal,
  className,
  lineItems,
  stripeCharge,
  paymentTier,
  category,
  onChangeSelection,
  paymentMethod,
  disableInteractions
}) {
  const amountTotalWithDollarSign = ensureCurrencyIsPresent(amountTotal);

  function renderLineItem(lineItem) {
    const itemType = lineItem.type;
    const props = {
      amountInDollars: lineItem.amount_in_dollars,
      description: lineItem.description,
      disableInteractions,
      discountedPriceInDollars: lineItem.discountedPriceInDollars,
      itemType,
      longDescription: lineItem.long_description,
      tooltipDescription: lineItem.tooltipDescription
    };

    if (
      category === PAYMENT_REQUEST_CATEGORIES.DEPOSIT &&
      lineItem.deposit_description
    ) {
      return (
        <StripeChargeModalDepositLineItem
          {...props}
          tooltipDescription={lineItem.deposit_description}
          paymentMethod={paymentMethod}
        />
      );
    }

    switch (itemType) {
      case PROCESSING_FEE:
        return (
          <StripeChargeModalPaymentProcessingLineItem
            {...props}
            paymentMethod={paymentMethod}
          />
        );
      case FEE:
        return (
          <StripeChargeModalPlatformFeeLineItem
            {...props}
            paymentTier={paymentTier}
            subtotalInDollars={amountSubtotal}
            dogPayment={category === PAYMENT_REQUEST_CATEGORIES.DOG_PAYMENT}
            onChangeSelection={tier => onChangeSelection?.(tier)}
          />
        );
      case CHARGE:
      default:
        return <StripeChargeModalPaymentSummaryLineItemV2 {...props} />;
    }
  }

  return (
    <div className={classnames('StripeChargeModalPaymentSummaryV3', className)}>
      <ul>
        {lineItems.map((item, idx) => (
          <li className="bb b--light-gray" key={idx}>
            {renderLineItem(item)}
          </li>
        ))}
      </ul>
      <div className="StripeChargeModalPaymentSummaryV3__total flex flex-column f3 fw-medium-ns items-end ph4">
        <div
          className={classnames('flex fw-medium flex-row', {
            'tr tl-l': !stripeCharge
          })}
        >
          <p className="pr2">Total:</p>
          <p
            className="font-serif f4 fw-medium"
            data-test-id="StripeChargeModalPaymentSummaryV3-total"
          >
            {amountTotalWithDollarSign}
          </p>
        </div>
        <div>
          {stripeCharge && (
            <p className="mt3 black-50 fw-normal nowrap">
              {stripeCharge.account_name} &ndash;{stripeCharge.account_digits}
              &nbsp;|&nbsp;
              {getDateString(new Date(stripeCharge.created_at))}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

StripeChargeModalPaymentSummaryV3.propTypes = {
  amountTotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  amountSubtotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  className: PropTypes.string,
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      amount_in_dollars: PropTypes.string.isRequired,
      type: PropTypes.oneOf(Object.values(LINE_ITEM_TYPES)).isRequired,
      discount_price_in_dollars: PropTypes.string,
      deposit_description: PropTypes.string
    })
  ).isRequired,
  paymentMethod: PropTypes.string,
  stripeCharge: PropTypes.shape({
    account_name: PropTypes.string,
    account_digits: PropTypes.string,
    created_at: PropTypes.string
  }),
  paymentTier: PropTypes.oneOf(Object.values(PAYMENT_TIER_TYPE)).isRequired,
  category: PropTypes.oneOf(Object.values(PAYMENT_REQUEST_CATEGORIES))
    .isRequired,
  onChangeSelection: PropTypes.func,
  disableInteractions: PropTypes.bool
};

StripeChargeModalPaymentSummaryV3.defaultProps = {
  className: null,
  stripeCharge: null,
  disableInteractions: false,
  onChangeSelection: null,
  paymentMethod: null
};
